import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowRight,
  MapPin,
  CheckCircle2,
  Building2,
  GraduationCap,
  Dumbbell,
  Store,
  Hotel,
  Factory,
  Stethoscope,
  Warehouse,
} from "lucide-react";
import { slugify } from "@/data/states";
import { SF_NEIGHBORHOODS, getSfNeighborhood } from "@/data/sf-neighborhoods";
import { ogImageMeta } from "@/lib/og-image";

export const Route = createFileRoute("/states/california/san-francisco/$neighborhood")({
  loader: ({ params }) => {
    const town = getSfNeighborhood(params.neighborhood);
    if (!town) throw notFound();
    return { town };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { town } = loaderData;
    const title = `Free Vending Machines in ${town}, San Francisco | $0 Placement & Service`;
    const desc = `Get a FREE vending machine for your ${town}, San Francisco business. Full-service install, restocking & maintenance at zero cost. Apply now.`;
    const url = `https://vendingmachinefree.com/states/california/san-francisco/${slugify(town)}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        ...ogImageMeta(),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              name: `Vending Machine Free.com — ${town}, San Francisco`,
              areaServed: { "@type": "Place", name: `${town}, San Francisco, CA` },
              description: desc,
            },
            {
              "@context": "https://schema.org",
              "@type": "Service",
              serviceType: "Free Vending Machine Placement",
              areaServed: { "@type": "Place", name: `${town}, San Francisco, CA` },
              provider: { "@type": "Organization", name: "Vending Machine Free.com" },
            },
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: FAQS(town).map(({ q, a }) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
              })),
            },
          ]),
        },
      ],
    };
  },
  component: NeighborhoodPage,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <Link to="/states/california/san-francisco" className="text-safety">
        Back to San Francisco
      </Link>
    </div>
  ),
  errorComponent: () => <div className="p-10">Error.</div>,
});

const SERVE = [
  { icon: Building2, label: "Offices" },
  { icon: GraduationCap, label: "Schools" },
  { icon: Dumbbell, label: "Gyms" },
  { icon: Store, label: "Retail Stores" },
  { icon: Hotel, label: "Hotels" },
  { icon: Factory, label: "Factories" },
  { icon: Stethoscope, label: "Medical Centers" },
  { icon: Warehouse, label: "Warehouses" },
];

const FAQS = (town: string) => [
  {
    q: `How do I get a free vending machine in ${town}?`,
    a: `Apply through our short form. A local San Francisco operator reviews your ${town} location within 24 hours, schedules a site survey, and installs your free vending machine — typically within 1–2 weeks.`,
  },
  {
    q: `Do you install vending machines in San Francisco?`,
    a: `Yes. We install, deliver, and service vending machines across every San Francisco neighborhood including ${town}. Installation, machine, and ongoing service are 100% free for qualified businesses.`,
  },
  {
    q: `What types of vending machines are available?`,
    a: `Snack machines, cold beverage machines (Coca-Cola, Pepsi), combo snack-and-drink machines, healthy vending, and coffee machines — all modern touchscreen units with tap-to-pay.`,
  },
  {
    q: `Are vending machines profitable in ${town}?`,
    a: `For your business, vending is pure benefit — no cost and a free employee/visitor amenity. We earn revenue from product sales, which is why placement is free in high-traffic ${town} locations.`,
  },
  {
    q: `How fast is installation?`,
    a: `Most ${town} sites are surveyed within 48 hours and have a vending machine installed within 1–2 weeks of approval.`,
  },
];

function NeighborhoodPage() {
  const { town } = Route.useLoaderData();
  const faqs = FAQS(town);
  const otherTowns = SF_NEIGHBORHOODS.filter((t) => t !== town).slice(0, 8);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background sticky top-0 z-40">
        <div className="container-x flex h-16 items-center justify-between">
          <Link to="/" className="font-display text-xl uppercase">
            Vending Machine Free<span className="text-safety">.com</span>
          </Link>
          <Link
            to="/"
            hash="contact"
            className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground"
          >
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <section className="bg-charcoal text-charcoal-foreground py-16">
        <div className="container-x">
          <nav className="text-xs text-white/60 mb-4">
            <Link to="/" className="hover:text-safety">
              Home
            </Link>{" "}
            /{" "}
            <Link to="/coverage" className="hover:text-safety">
              USA Map
            </Link>{" "}
            /{" "}
            <Link
              to="/states/$state"
              params={{ state: "california" }}
              className="hover:text-safety"
            >
              California
            </Link>{" "}
            /{" "}
            <Link to="/states/california/san-francisco" className="hover:text-safety">
              San Francisco
            </Link>{" "}
            / <span className="text-white">{town}</span>
          </nav>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
            <MapPin className="h-4 w-4" /> {town}, San Francisco
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl uppercase">
            Free Vending Machines in {town}, San Francisco
          </h1>
          <p className="mt-4 text-white/80 max-w-2xl text-lg">
            Get FREE vending machine placement, installation, restocking, insurance, and ongoing
            maintenance for businesses in {town}, San Francisco. Offices, schools, gyms, retail
            stores, hotels, factories, medical centers, and warehouses all qualify for free
            placement.
          </p>
          <a
            href="#apply"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3 font-semibold text-safety-foreground"
          >
            Get a Vending Machine FREE for your business in {town} Now{" "}
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="py-16">
        <div className="container-x max-w-5xl">
          <h2 className="font-display text-3xl uppercase">Who We Serve in {town}</h2>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {SERVE.map(({ icon: Icon, label }) => (
              <div key={label} className="rounded-lg bg-card border border-border p-5 text-center">
                <Icon className="h-8 w-8 text-safety mx-auto" />
                <div className="mt-2 font-semibold">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-secondary">
        <div className="container-x max-w-3xl">
          <h2 className="font-display text-3xl uppercase">Why Choose Us in {town}</h2>
          <ul className="mt-8 grid sm:grid-cols-2 gap-3">
            {[
              "Zero cost — machine, install, service all free",
              "Local San Francisco operators",
              "Modern touchscreen, tap-to-pay machines",
              "Fast installation (1–2 weeks)",
              "Full restocking & maintenance included",
              `Local route drivers serving ${town}`,
            ].map((t) => (
              <li
                key={t}
                className="flex gap-2 text-sm bg-card border border-border rounded-md p-3"
              >
                <CheckCircle2 className="h-5 w-5 text-safety flex-shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-16">
        <div className="container-x max-w-3xl">
          <h2 className="font-display text-3xl uppercase">Frequently Asked Questions</h2>
          <div className="mt-8 divide-y divide-border border border-border rounded-xl bg-card">
            {faqs.map(({ q, a }) => (
              <details key={q} className="group p-6">
                <summary className="flex cursor-pointer items-center justify-between gap-4 font-semibold list-none">
                  <span>{q}</span>
                  <span className="h-7 w-7 rounded-md bg-secondary flex items-center justify-center text-safety group-open:rotate-45 transition">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section id="apply" className="py-16 bg-secondary">
        <div className="container-x text-center">
          <h2 className="font-display text-3xl uppercase">
            Apply for a Free Vending Machine in {town}
          </h2>
          <p className="mt-3 text-muted-foreground">Local SF approval in as little as 48 hours.</p>
          <Link
            to="/"
            hash="contact"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-7 py-3.5 font-semibold text-safety-foreground shadow-[var(--shadow-cta)]"
          >
            Get a Vending Machine FREE for your business in {town} Now{" "}
            <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="mt-10 flex flex-wrap justify-center gap-5 text-sm">
            <Link
              to="/states/california/san-francisco"
              className="text-steel hover:text-safety font-semibold"
            >
              ← Back to San Francisco
            </Link>
            <Link
              to="/states/$state"
              params={{ state: "california" }}
              className="text-steel hover:text-safety font-semibold"
            >
              Back to California
            </Link>
            <Link to="/coverage" className="text-steel hover:text-safety font-semibold">
              Back to USA Map
            </Link>
            <Link to="/" className="text-steel hover:text-safety font-semibold">
              Home
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container-x">
          <h3 className="font-display text-2xl uppercase text-center">Explore Other SF Towns</h3>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {otherTowns.map((t) => (
              <Link
                key={t}
                to="/states/california/san-francisco/$neighborhood"
                params={{ neighborhood: slugify(t) }}
                title={`Free vending machines in ${t}, San Francisco`}
                className="group flex items-center gap-3 rounded-lg border border-border bg-card p-4 hover:border-safety hover:shadow-md transition"
              >
                <div className="h-10 w-10 rounded-md bg-steel/10 text-steel flex items-center justify-center group-hover:bg-safety/15 group-hover:text-safety transition">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-semibold text-sm">{t}</div>
                  <div className="text-xs text-muted-foreground">SF · Free</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
